// API client for BGS Core
const API_BASE_URL = '/api'

export interface Item {
  id: string
  name: string
  sku: string
  ic: string
  description: string
  condition: string
  quantity: number
  target_price: number
  cost: number
  category_id: string
  location_id: string
  tote_id: string
  status: string
  created_at: string
  updated_at: string
  category_name?: string
  location_name?: string
  tote_name?: string
  // Additional fields for item details
  barcode?: string
  brand?: string
  model?: string
  notes?: string
  reserved?: number
  weight_lbs?: number
  weight_oz?: number
  total_weight_oz?: number
  length_cm?: number
  width_cm?: number
  height_cm?: number
  volume_cm3?: number
  shipping_instructions?: string
  package_type?: string
  source?: string
  acquisition_date?: string
  last_moved_at?: string
  sale_price?: number
  available?: boolean
  custom_fields?: any[]
  sales_history?: any[]
  history?: any[]
}

export interface Category {
  id: string
  name: string
  description?: string
}

export interface Location {
  id: string
  name: string
  description?: string
  totes?: Array<{
    id: string
    name: string
  }>
}

export interface SearchParams {
  search?: string
  sku?: string
  category?: string
  location?: string
  status?: string
  condition?: string
  listed?: string
  intakeFrom?: string
  intakeTo?: string
  listedFrom?: string
  listedTo?: string
  soldFrom?: string
  soldTo?: string
  limit?: number
  offset?: number
  sort?: string
  order?: 'asc' | 'desc'
}

// Items API
export const itemsApi = {
  getAll: async (params: SearchParams = {}): Promise<{ items: Item[]; total: number }> => {
    const searchParams = new URLSearchParams()
    
    // Map frontend parameters to API parameters
    if (params.search) searchParams.append('q', params.search)
    if (params.sku) searchParams.append('sku', params.sku)
    if (params.category) searchParams.append('category', params.category)
    if (params.location) searchParams.append('location', params.location)
    if (params.status) searchParams.append('status', params.status)
    if (params.condition) searchParams.append('condition', params.condition)
    if (params.listed) searchParams.append('listed', params.listed)
    if (params.intakeFrom) searchParams.append('intakeDateFrom', params.intakeFrom)
    if (params.intakeTo) searchParams.append('intakeDateTo', params.intakeTo)
    if (params.listedFrom) searchParams.append('listedDateFrom', params.listedFrom)
    if (params.listedTo) searchParams.append('listedDateTo', params.listedTo)
    if (params.soldFrom) searchParams.append('soldDateFrom', params.soldFrom)
    if (params.soldTo) searchParams.append('soldDateTo', params.soldTo)
    if (params.limit) searchParams.append('limit', params.limit.toString())
    if (params.offset) searchParams.append('offset', params.offset.toString())
    if (params.sort) searchParams.append('sortBy', params.sort)
    if (params.order) searchParams.append('sortOrder', params.order)
    
    // Add operation parameter
    searchParams.append('op', 'search')
    
    const response = await fetch(`${API_BASE_URL}/central-optimized?${searchParams}`)
    if (!response.ok) {
      throw new Error('Failed to fetch items')
    }
    return response.json()
  },

  getById: async (id: string): Promise<Item> => {
    const response = await fetch(`${API_BASE_URL}/items-pg/${id}`)
    if (!response.ok) {
      throw new Error('Failed to fetch item')
    }
    return response.json()
  },

  create: async (item: Partial<Item>): Promise<Item> => {
    const response = await fetch(`${API_BASE_URL}/items-pg`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(item),
    })
    if (!response.ok) {
      throw new Error('Failed to create item')
    }
    return response.json()
  },

  update: async (id: string, item: Partial<Item>): Promise<Item> => {
    const response = await fetch(`${API_BASE_URL}/items-pg/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(item),
    })
    if (!response.ok) {
      throw new Error('Failed to update item')
    }
    return response.json()
  },

  delete: async (id: string): Promise<void> => {
    const response = await fetch(`${API_BASE_URL}/items-pg/${id}`, {
      method: 'DELETE',
    })
    if (!response.ok) {
      throw new Error('Failed to delete item')
    }
  },
}

// Categories API
export const categoriesApi = {
  getAll: async (): Promise<{ categories: Category[] }> => {
    const response = await fetch(`${API_BASE_URL}/central-optimized?op=categories`)
    if (!response.ok) {
      throw new Error('Failed to fetch categories')
    }
    return response.json()
  },
}

// Locations API
export const locationsApi = {
  getAll: async (): Promise<{ locations: Location[] }> => {
    const response = await fetch(`${API_BASE_URL}/central-optimized?op=locations`)
    if (!response.ok) {
      throw new Error('Failed to fetch locations')
    }
    return response.json()
  },
}

// Statuses API
export const statusesApi = {
  getAll: async (): Promise<{ statuses: string[] }> => {
    const response = await fetch(`${API_BASE_URL}/central-optimized?op=statuses`)
    if (!response.ok) {
      throw new Error('Failed to fetch statuses')
    }
    return response.json()
  },
}

// Conditions API
export const conditionsApi = {
  getAll: async (): Promise<{ conditions: string[] }> => {
    const response = await fetch(`${API_BASE_URL}/central-optimized?op=conditions`)
    if (!response.ok) {
      throw new Error('Failed to fetch conditions')
    }
    return response.json()
  },
}

// Item Details API
export const itemDetailsApi = {
  getById: async (id: string): Promise<{ success: boolean; item: Item }> => {
    const response = await fetch(`${API_BASE_URL}/central-optimized?op=item&item_id=${id}`)
    if (!response.ok) {
      throw new Error('Failed to fetch item details')
    }
    return response.json()
  },
  
  update: async (id: string, data: Partial<Item>): Promise<{ success: boolean; item: Item }> => {
    const response = await fetch(`${API_BASE_URL}/central-optimized?op=update-item`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ id, ...data }),
    })
    if (!response.ok) {
      throw new Error('Failed to update item')
    }
    return response.json()
  },
  
  getImages: async (id: string): Promise<{ success: boolean; images: any[] }> => {
    const response = await fetch(`${API_BASE_URL}/items/${id}/images`)
    if (!response.ok) {
      throw new Error('Failed to fetch item images')
    }
    return response.json()
  }
}

// Health check
export const healthApi = {
  check: async (): Promise<{ status: string; timestamp: string }> => {
    const response = await fetch(`${API_BASE_URL}/health`)
    if (!response.ok) {
      throw new Error('Health check failed')
    }
    return response.json()
  },
}
